import React, { useState } from "react";
import { submitAdminCorrection } from "../lib/demographic";

/**
 * AdminCorrectionForm
 *
 * Admin-only, immediate-apply correction on an already-canonical
 * patient. Unlike verification, this has NO separate approve step —
 * submitting applies the change directly. correction_notes is
 * required and shown in the audit trail, since this bypasses the
 * normal review path.
 *
 * Expects patientId as a prop — this component assumes it's launched
 * from a context where a specific patient is already selected (e.g.
 * Patient Roster or a patient profile view). It does not do its own
 * patient lookup.
 */
export default function AdminCorrectionForm({
  patientId,
  initialValues,
  onBack,
  onResolved,
}) {
  const [form, setForm] = useState({
    first_name: initialValues?.first_name || "",
    last_name: initialValues?.last_name || "",
    date_of_birth: initialValues?.date_of_birth || "",
    sex: initialValues?.sex || "",
    external_mrn: initialValues?.external_mrn || "",
    correction_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit() {
    if (
      !form.first_name ||
      !form.last_name ||
      !form.date_of_birth ||
      !form.sex
    ) {
      setError("First name, last name, date of birth, and sex are required.");
      return;
    }
    if (!form.correction_notes.trim()) {
      setError(
        "Correction notes are required — this change applies immediately and is logged.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await submitAdminCorrection(patientId, form);
      onResolved();
    } catch (err) {
      setError(err.response?.data?.message || "Correction failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
      >
        ← Back
      </button>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-1">
          Apply demographic correction
        </h3>
        <p className="text-xs text-amber-600 mb-4">
          This applies immediately — there is no separate approval step.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
              First name
            </label>
            <input
              value={form.first_name}
              onChange={(e) => update("first_name", e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Last name
            </label>
            <input
              value={form.last_name}
              onChange={(e) => update("last_name", e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Date of birth
            </label>
            <input
              type="date"
              value={form.date_of_birth}
              onChange={(e) => update("date_of_birth", e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Sex
            </label>
            <input
              value={form.sex}
              onChange={(e) => update("sex", e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
              External MRN
            </label>
            <input
              value={form.external_mrn}
              onChange={(e) => update("external_mrn", e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Correction notes (required — logged in audit trail)
            </label>
            <textarea
              value={form.correction_notes}
              onChange={(e) => update("correction_notes", e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 bg-white text-gray-900 text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={busy}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-lg transition-colors"
        >
          {busy ? "Applying…" : "Apply Correction"}
        </button>
      </div>
    </div>
  );
}
